import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { filter, map, takeWhile, tap } from 'rxjs/operators';
import * as fromAccount from '../../../account/store';
import * as fromSettings from '../../../company-setting';
import { CalcType } from '../../../lookups/CalcType';
import * as fromCommon from '../../../shared';
import * as fromShared from '../../../shared';
import { Process, SelectItem, uniqBy } from '../../../shared';
import * as fromLoading from '../../../shared/loading';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import * as fromStore from '../store';
import * as fromThirdPartyPayroll from '../../third-party/store';
import { PayrollItem } from '../store';
import * as fromEmployeeActions from '../store/payroll-item-employee.actions';
import { PayrollItemEmployee } from '../store/payroll-item-employee.model';
import * as fromEmployeeSelectors from '../store/payroll-item-employee.selectors';

@Component({
    selector: 'ef-payroll-item-feature',
    templateUrl: './payroll-item-feature.component.html',
    styleUrls: ['./payroll-item-feature.component.scss'],
    standalone: false
})
export class PayrollItemFeatureComponent implements OnInit, OnDestroy {
    process$: Observable<Process>;
    qbOnline$: Observable<boolean>;
    externalIntegration$: Observable<boolean>;
    loading$: Observable<boolean>;
    payrollItems$: Observable<PayrollItem[]>;
    payrollItemsSelectItems$: Observable<SelectItem[]>;
    payrollItemTypeSelectItems$: Observable<SelectItem[]>;
    payCodeSelectItems$: Observable<SelectItem[]>;
    payrollItemEmployees$: Observable<PayrollItemEmployee[]>;
    laborTypes$: Observable<CalcType[]>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;
    quickbooksOnline$: Observable<boolean>;
    accountSelectItems$: Observable<SelectItem[]>;
    active = true;
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);

    employeesPayrollItemsSelectItems$: Observable<SelectItem[]>;
    employeesSelectItems$: Observable<SelectItem[]>;
    showInactivePayrollItems = false;
    showInactivePayrollItems$ = new BehaviorSubject<boolean>(false);
    constructor(
        private log: LoggingService,
        private store: Store<fromStore.State>
    ) {}

    ngOnInit() {
        this.store.dispatch(fromStore.getPayrollItems());
        this.store.dispatch(fromThirdPartyPayroll.getPayrollCodes());
        this.store.dispatch(fromStore.getLaborTypes());
        this.store.dispatch(fromAccount.getAccounts({}));
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'ExternalEmployeeSource'
            })
        );
        this.setupObservables();
        this.setupSubscriptions();
    }
    ngOnDestroy() {
        this.active = false;
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.qbOnline$ = this.store.pipe(select(fromSettings.isQBOEnabled));
        this.externalIntegration$ = this.store.pipe(
            select(
                fromSettings.getSettingBooleanValue('ExternalEmployeeSource')
            )
        );
        const allPayrollItems$ = this.store.pipe(
            select(fromStore.selectAll),
            map(items => {
                return items.map(item => {
                    return {
                        ...item,
                        isValid() {
                            return (
                                !!this.PayrollItem &&
                                this.PayrollItem.length > 0 &&
                                !!this.AccountListID &&
                                this.AccountListID.length > 0 &&
                                !!this.PayrollItemType &&
                                this.PayrollItemType.length > 0
                            );
                        }
                    } as PayrollItem;
                }) as PayrollItem[];
            }),
            map(items =>
                clone(items).sort((a: PayrollItem, b: PayrollItem) =>
                    a.PayrollItem?.toLocaleLowerCase()?.localeCompare(
                        b.PayrollItem?.toLocaleLowerCase()
                    )
                )
            )
        );
        this.payrollItems$ = allPayrollItems$;
        this.payrollItemsSelectItems$ = this.payrollItems$.pipe(
            map(items =>
                items.map(item => ({
                    label: item.PayrollItem,
                    value: item.PIListID
                }))
            )
        );
        this.payrollItemEmployees$ = this.store
            .pipe(select(fromEmployeeSelectors.selectAll))
            .pipe(
                map(items =>
                    clone(items).sort(
                        (a: PayrollItemEmployee, b: PayrollItemEmployee) =>
                            a.Employeename.toLocaleLowerCase().localeCompare(
                                b.Employeename.toLocaleLowerCase()
                            )
                    )
                )
            );
        this.employeesPayrollItemsSelectItems$ =
            this.payrollItemEmployees$.pipe(
                map(items =>
                    items.map(item => ({
                        label: item.Payrollitem,
                        value: item.Pilistid
                    }))
                )
            );
        this.employeesSelectItems$ = this.payrollItemEmployees$.pipe(
            map(items =>
                items.map(item => ({
                    label: item.Employeename,
                    value: item.Employeelistid
                }))
            )
        );
        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors),
            tap(selected =>
                this.log.verbose({
                    type: 'PayrollItemFeatureComponent:selectedError$',
                    selected
                })
            )
        );
        const errors$ = this.store.pipe(select(fromShared.Errors.getErrors));
        this.selectedErrorWithStatus$ = combineLatest([
            this.selectedError$,
            errors$,
            this.payrollItems$.pipe(filter(x => !!x))
        ]).pipe(
            tap(([selected, errors]) =>
                this.log.verbose({
                    type: 'PayrollItemFeatureComponent:errors$',
                    errors,
                    selected
                })
            ),
            map(
                ([selected, errors]) =>
                    errors.filter(
                        e =>
                            e.MsgEntityID === selected.MsgEntityID &&
                            e.MsgProcessID === selected.MsgProcessID
                    )[0]
            ),
            tap(selected =>
                this.log.verbose({
                    type: 'PayrollItemFeatureComponent:errors$:filtered',
                    selected
                })
            )
        );

        this.laborTypes$ = this.store.pipe(
            select(fromStore.selectLaborTypeProperty)
        );
        this.loading$ = this.store.pipe(select(fromLoading.getLoading));

        this.quickbooksOnline$ = this.store.pipe(
            select(fromSettings.isQBOEnabled)
        );
        const payCodes$ = this.store.pipe(
            select(fromThirdPartyPayroll.selectPayrollCodes)
        );
        this.payCodeSelectItems$ = payCodes$.pipe(
            map(items =>
                items.map(item => ({
                    label: `${item.Paycodedesc ?? item.Paycode} (${
                        item.Paycode
                    })`,
                    value: item.Paycode
                }))
            )
        );
        this.payrollItemTypeSelectItems$ = this.payrollItems$.pipe(
            map(items =>
                uniqBy(
                    items
                        .map(item => ({
                            label: item.PayrollItemType,
                            value: item.PayrollItemType
                        }))
                        .concat([
                            { label: 'Salary', value: 'Salary' },
                            { label: 'Hourly', value: 'Hourly' }
                        ]),
                    x => x.value
                )
            )
        );
        this.accountSelectItems$ = this.store.pipe(
            select(fromAccount.selectAll),
            map(items =>
                items.map(item => ({
                    label: item.AccountCode,
                    value: item.AccountListID
                }))
            )
        );
    }
    setupSubscriptions() {
        combineLatest([this.process$, this.ShowErrorSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
    }
    onSave($event: PayrollItem[]) {
        this.log.verbose({
            type: 'PayrollItemFeatureComponent:onSave',
            $event
        });
        this.store.dispatch(fromStore.putPayrollItems({ items: $event }));
    }
    onShowError($event) {
        this.ShowErrorSubject.next($event);
    }
    onSelect($event: PayrollItem) {
        this.log.verbose({
            type: 'PayrollItemFeatureComponent:onSelect',
            $event
        });
        if ($event) {
            this.store.dispatch(
                fromEmployeeActions.getPayrollItemEmployees({
                    PIListID: $event.PIListID
                })
            );
        }
    }
    onShowInactiveChange(show: boolean) {
        this.showInactivePayrollItems = show;
        this.showInactivePayrollItems$.next(show);
        this.store.dispatch(fromStore.getPayrollItems(!show));
    }
}
